"use client";
// Requirements summary:
// - Supports OTP, password, magic-link, MFA handoff, and contextual sign-in
//   redirects without allowing unsafe external redirect targets.
// - Athlete dashboard deep-link logins set a one-time staff-shell hint so
//   gym staff do not appear to become a parent account after authentication.
// - Already-authenticated users are redirected away from /signin so dev-server
//   restarts do not leave a valid cookie session stuck on the sign-in form.
// - Placement-reveal links can request one-time sign-out so a shared browser
//   does not keep the previous parent account attached to the accept flow.
import { TextField, PasswordField, PhoneField, EmailField } from "@/components/form/fields";
import Button from "@/components/ui/button/Button";
import GoogleMaterialIcon from "@/components/ui/GoogleMaterialIcon";
import { useNotification } from "@/components/ui/notification";
import { supabase } from "@/utils/supabase/client";
import { createLogger } from "@/utils/logger/devLogger";
import GradientLogo from "@/components/ui/GradientLogo";
import Turnstile from 'react-turnstile';
import { validateEmail } from "@/components/form/validation";
import { normalizePhoneForAuth } from "@/utils/phone/normalizePhone";
import {
  OTP_CODE_LENGTH,
  applyOtpDigits,
  createEmptyOtpCode,
  extractOtpDigits,
  getOtpFocusIndex,
} from "@/utils/auth/otpInput";
import { FEATURE_FLAGS } from "@/utils/config/featureFlags";
import { validateInternalPath } from "@/utils/url/validateInternalPath";
import { gymCatalogHrefFromRedirect } from "@/utils/auth/signupEntry";
import { isOrgSetupDestination } from "@/utils/auth/orgSetupLink";
import { isPasswordResetNext } from "@/utils/auth/passwordResetLink";
import { AUTH_LINK_LIFETIME_TEXT } from "@cheervue/auth-link-lifetime";
import {
  FORCE_STAFF_PERSONA_AFTER_LOGIN_KEY,
  shouldForceStaffPersonaAfterLogin,
} from "@/utils/auth/forceStaffPersona";
import {
  getStoredBrandTheme,
  safeSessionStorageRemoveItem,
  safeSessionStorageSetItem,
} from "@/utils/storage/safeLocalStorage";

import Link from "next/link";
import React, { useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/context/AuthContext";

const logger = createLogger('SignInForm');

export default function SignInForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showResendVerification, setShowResendVerification] = useState(false);
  // Which flavour of dead one-time link brought them here, or null for none.
  // 'setup' is someone who has never had a password; 'reset' is someone who has
  // one and asked to replace it. They get the same panel with opposite wording,
  // and telling them apart is the point — see expiredLinkPanelCopy below.
  const [expiredLinkPanel, setExpiredLinkPanel] = useState<'setup' | 'reset' | null>(null);
  const showResendSetup = expiredLinkPanel !== null;
  const [showResendSuccess, setShowResendSuccess] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isForceSigningOut, setIsForceSigningOut] = useState(false);
  const [hasHandledForceSignOut, setHasHandledForceSignOut] = useState(false);
  const [brandTheme, setBrandTheme] = useState<'v1' | 'v2'>('v2');
  
  // OTP-specific state
  const [loginMode, setLoginMode] = useState<'password' | 'otp'>('otp');
  const [otpChannel, setOtpChannel] = useState<'email' | 'sms'>('email');
  const [identifier, setIdentifier] = useState(''); // Email or phone number
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState(createEmptyOtpCode());
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [botField, setBotField] = useState("");
  const otpInputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const isOtpVerifyingRef = useRef(false);
  const forceSignOutHandledRef = useRef(false);
  
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, user, isLoading: authLoading } = useAuth();
  const { showNotification } = useNotification();

  // Contextual header copy when the parent was bounced here from a booking/enroll/register
  // deep link — tells them *why* they're signing in rather than just showing a generic wall.
  // safeRedirectTo is validated + decoded in one step and never throws on a malformed param,
  // so we can use it directly in renders (including the gym link below) without try/catch.
  const rawRedirectTo = searchParams.get('redirectTo');
  const safeRedirectTo = validateInternalPath(rawRedirectTo, '');
  const forceSignOutRequested = searchParams.get('forceSignOut') === 'true';
  const invitedEmailParam = (searchParams.get('email') || '').toLowerCase().trim();
  const invitedEmail = validateEmail(invitedEmailParam).valid ? invitedEmailParam : '';
  const isParentInvitationRedirect = safeRedirectTo.startsWith('/parent-invitations/accept/');
  const bookingContext: 'booking' | 'enrollment' | 'registration' | null = (() => {
    if (!safeRedirectTo) return null;
    if (/\/programs\/[^/]+\/private-lessons\/[^/]+\/book/.test(safeRedirectTo)) return 'booking';
    if (/\/programs\/[^/]+\/[^/]+\/[^/]+\/enroll/.test(safeRedirectTo)) return 'enrollment';
    if (/\/programs\/[^/]+\/.*\/register/.test(safeRedirectTo)) return 'registration';
    // Tryout event registrations live outside /programs/ at /register-tryout/...
    if (/\/register-tryout\/[^/]+\/[^/]+\/register/.test(safeRedirectTo)) return 'registration';
    return null;
  })();

  // Signup no longer starts here. Accounts must be created from a gym's catalog
  // page (its Create Account button carries the organizationId) or from a gym
  // invite, so a generic /register link would only dead-end. When the visitor was
  // bounced here from a gym page we can still point them back at it.
  // safeRedirectTo is validated above, so this is safe by construction.
  const gymCatalogHref = gymCatalogHrefFromRedirect(safeRedirectTo);

  const rememberStaffPersonaForRedirect = (destination: string) => {
    if (shouldForceStaffPersonaAfterLogin(destination)) {
      // Direct athlete URL login safety: if a staff user had parent mode saved
      // from a prior visit, the post-login athlete deep link should still open
      // in the gym/staff shell. Parent-only users ignore this later because
      // they do not have the staff persona available.
      safeSessionStorageSetItem(FORCE_STAFF_PERSONA_AFTER_LOGIN_KEY, 'true');
    }
  };

  const forgetStaffPersonaForRedirect = (destination: string) => {
    if (shouldForceStaffPersonaAfterLogin(destination)) {
      safeSessionStorageRemoveItem(FORCE_STAFF_PERSONA_AFTER_LOGIN_KEY);
    }
  };

  const changePasswordHref = safeRedirectTo
    ? `/auth/change-password?redirectTo=${encodeURIComponent(safeRedirectTo)}`
    : '/auth/change-password';

  // Placement reveal can be opened from a shared family device where another
  // parent is still signed in. Honor the one-time forceSignOut flag before the
  // existing-session redirect below, then remove only that flag from the URL.
  useEffect(() => {
    if (!forceSignOutRequested || forceSignOutHandledRef.current) return;

    forceSignOutHandledRef.current = true;
    setHasHandledForceSignOut(true);
    setIsForceSigningOut(true);
    setLoading(true);
    setError(null);
    setMessage('Signing you out so the right parent can continue.');

    const cleanedParams = new URLSearchParams(searchParams.toString());
    cleanedParams.delete('forceSignOut');
    const cleanQuery = cleanedParams.toString();
    const cleanSignInPath = cleanQuery ? `/signin?${cleanQuery}` : '/signin';
    let isMounted = true;

    supabase.auth.signOut({ scope: 'local' })
      .then(({ error: signOutError }) => {
        if (signOutError) {
          throw signOutError;
        }

        logger.info('Cleared existing session before forced sign-in handoff', {
          redirectTo: safeRedirectTo || null,
        });
        if (!isMounted) return;
        router.replace(cleanSignInPath, { scroll: false });
      })
      .catch((err) => {
        logger.error('Forced sign-out before sign-in failed:', err);
        if (!isMounted) return;
        setMessage(null);
        setError('We could not switch accounts automatically. Please sign out, then open the placement link again.');
      })
      .finally(() => {
        if (!isMounted) return;
        setIsForceSigningOut(false);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [forceSignOutRequested, router, safeRedirectTo, searchParams]);

  // If a dev server restart leaves Supabase's cookie session intact, the user
  // can land on /signin already authenticated. Send them forward instead of
  // letting a second password submit hang on the stale sign-in screen.
  useEffect(() => {
    if (authLoading || !user || forceSignOutRequested || isForceSigningOut || hasHandledForceSignOut) return;

    if (typeof window !== 'undefined' && window.location.hash) {
      const hashType = new URLSearchParams(window.location.hash.substring(1)).get('type');
      if (hashType === 'signup' || hashType === 'magiclink' || hashType === 'email' || hashType === 'recovery') {
        return;
      }
    }

    const destination = validateInternalPath(searchParams.get('redirectTo'), '/dashboard');
    logger.info('Existing session found on sign-in page, redirecting authenticated user', {
      userId: user.id,
      destination
    });
    rememberStaffPersonaForRedirect(destination);
    router.replace(destination);
  }, [authLoading, forceSignOutRequested, hasHandledForceSignOut, isForceSigningOut, router, searchParams, user]);

  // Requirements summary: blocked browser storage should not block sign-in or magic-link handoff.
  useEffect(() => {
    if (getStoredBrandTheme() === 'v1') {
      setBrandTheme('v1');
    }
  }, []);

  // Parent invitation handoff: when someone is bounced from an invite to
  // sign-in, keep the invited email in the form so they do not have to infer
  // which address the gym invited.
  useEffect(() => {
    if (!invitedEmail) return;
    setEmail(invitedEmail);
    setIdentifier(invitedEmail);
    setOtpChannel('email');
  }, [invitedEmail]);

  // Handle implicit-flow hash fragments from Supabase auth redirects.
  // Production Supabase uses the implicit flow for email verification, which puts
  // tokens in the URL hash (#access_token=...&type=signup|magiclink|recovery).
  // The server-side callback route can't read hash fragments, so those requests
  // bounce to /signin?error=invalid_verification_link — but the browser preserves
  // the hash across the redirect, so we handle it client-side here.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.location.hash) return;

    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');

    if (type === 'recovery') {
      logger.info('Recovery hash detected on sign-in page, redirecting to reset-password');
      window.location.href = '/auth/reset-password' + window.location.hash;
      return;
    }

    if ((type === 'magiclink' || type === 'email' || type === 'signup') && accessToken) {
      const isSignup = type === 'signup';
      const refreshToken = hashParams.get('refresh_token');
      logger.info(`${isSignup ? 'Signup verification' : 'Magic link'} hash detected, establishing session...`);

      // Clear the false error query param that the server-side callback injected
      // and show a friendly message while we establish the session.
      setError(null);
      setMessage(isSignup ? 'Email verified! Signing you in...' : 'Signing you in...');
      window.history.replaceState(null, '', window.location.pathname);

      // validateInternalPath blocks `//evil.com` and other open-redirect payloads,
      // and safely handles malformed %-sequences without throwing.
      const destination = validateInternalPath(searchParams.get('redirectTo'), '/dashboard');
      rememberStaffPersonaForRedirect(destination);

      // Helper: redirect once session is established
      let hasRedirected = false;
      const completeSignIn = () => {
        if (hasRedirected) return;
        hasRedirected = true;
        subscription.unsubscribe();
        if (isSignup) {
          showNotification('success', 'Email Verified', 'Your email has been verified successfully. Welcome to CheerVue!');
        }
        router.push(destination);
      };

      // Listen for auth state changes. Check for SIGNED_IN and TOKEN_REFRESHED —
      // setSession() emits TOKEN_REFRESHED when there was a prior (expired) session.
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
          logger.info(`✅ ${isSignup ? 'Signup verification' : 'Magic link'} session established via ${event}, redirecting...`);
          completeSignIn();
        }
      });

      // Manually set the session from the hash tokens.
      // The PKCE-configured client may not auto-process implicit-flow hash tokens.
      if (refreshToken) {
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        }).then(({ data, error: sessionError }) => {
          if (sessionError) {
            forgetStaffPersonaForRedirect(destination);
            logger.error('Failed to set session from hash tokens:', sessionError);
            setMessage(null);
            setError('Verification failed. Please try signing in.');
            subscription.unsubscribe();
          } else if (data.session) {
            // setSession succeeded — redirect directly as fallback
            // (in case onAuthStateChange didn't fire the expected event)
            logger.info('✅ Session set from hash tokens, redirecting...');
            completeSignIn();
          }
        });
      } else {
        // No refresh token in hash — check if the client auto-processed the hash
        setTimeout(async () => {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              logger.info('✅ Session already established, redirecting...');
              completeSignIn();
            }
          } catch (err) {
            logger.error('Error checking existing session after hash auth:', err);
          }
        }, 500);
      }

      // Fallback timeout: if nothing worked after 5 seconds, check one more time
      const fallbackTimer = setTimeout(async () => {
        if (hasRedirected) return;
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            logger.info('✅ Fallback: session found, redirecting...');
            completeSignIn();
          } else {
            forgetStaffPersonaForRedirect(destination);
            logger.warn('Fallback: no session after 5s, showing sign-in');
            setMessage(null);
            setError('Could not complete sign-in automatically. Please sign in with your email and password.');
            subscription.unsubscribe();
          }
        } catch (err) {
          logger.error('Fallback session check failed:', err);
        }
      }, 5000);

      return () => {
        subscription.unsubscribe();
        clearTimeout(fallbackTimer);
      };
    }
  }, [router, searchParams, showNotification]);

  // OTP cooldown timer
  useEffect(() => {
    if (otpCooldown > 0) {
      const timer = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCooldown]);

  // Extract message and redirectTo from URL params
  useEffect(() => {
    // Skip error display when a signup hash fragment is present — the hash
    // handler above already cleared the false error and set a success message.
    if (typeof window !== 'undefined' && window.location.hash) {
      const hashType = new URLSearchParams(window.location.hash.substring(1)).get('type');
      if (hashType === 'signup') return;
    }

    const msgParam = searchParams.get('message');
    const registeredParam = searchParams.get('registered');
    const verifiedParam = searchParams.get('verified');
    const errorParam = searchParams.get('error');
    
    if (verifiedParam === 'true') {
      setMessage(msgParam || 'Email verified successfully! Please sign in to continue.');
      showNotification('success', 'Email Verified', 'Your email has been verified successfully.');
    } else if (errorParam === 'link_expired') {
      setError(msgParam || 'Your verification link has expired. Please request a new one.');
      
      // Which recovery to offer depends on whether this person already has a
      // password, and the only evidence on hand is where the dead link was
      // headed. A first-time setup link and a password reset are both
      // `type=recovery`, so the token type cannot separate them — the same
      // problem /auth/continue solves with the same two helpers.
      //
      // This used to be `next?.includes('/setup')`, which is true of BOTH:
      // resets go to `/setup`, and the owner path `/onboarding/setup-password`
      // contains it as a substring. Every established gym owner who asked to
      // reset a password was therefore offered a *setup* link and told to
      // finish setting up an account they had been using for months.
      //
      // Both helpers document that they compare an already-decoded,
      // open-redirect-checked path — the form the callback hands them. Reading
      // the query string raw here would quietly break that contract, so it goes
      // through the same validator. The '' fallback keeps a rejected value
      // falling through to the resend-verification branch, exactly as an
      // unrecognised `next` already did.
      const nextParam = validateInternalPath(searchParams.get('next'), '');
      if (isPasswordResetNext(nextParam)) {
        setExpiredLinkPanel('reset');
        setShowResendVerification(false);
      } else if (isOrgSetupDestination(nextParam)) {
        setExpiredLinkPanel('setup');
        setShowResendVerification(false);
      } else {
        // Total on purpose: this effect re-runs when the URL changes, and a
        // second link_expired arrival with a different `next` must not leave
        // the previous panel latched on.
        setExpiredLinkPanel(null);
        setShowResendVerification(true);
      }
    } else if (registeredParam === 'true') {
      setMessage('🎉 Your account has been created successfully! Please sign in to continue.');
    } else if (msgParam === 'please-signin-for-invitation') {
      setMessage('Please sign in to accept your gym invitation.');
    } else if (msgParam === 'account-created') {
      setMessage('🎉 Your account has been created successfully! Please sign in to get started.');
    } else if (msgParam === 'staff-account-created') {
      setMessage('🎉 Your staff account has been created successfully! Please sign in to access your dashboard.');
    } else if (msgParam) {
      setMessage(msgParam);
    }
  }, [searchParams, showNotification]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  // Use shared phone normalization utility (DRY compliance)
  // Note: normalizePhoneForAuth handles E.164 formatting and validation

  // Handle sending OTP
  const handleSendOTP = async () => {
    if (!identifier.trim()) {
      setError(otpChannel === 'email' ? 'Please enter your email address' : 'Please enter your phone number');
      return;
    }

    // Validate input based on channel and normalize
    let finalIdentifier: string;
    
    if (otpChannel === 'email') {
      // ✅ DRY COMPLIANCE: Use existing validateEmail utility
      const emailValidation = validateEmail(identifier, true);
      if (!emailValidation.valid) {
        setError(emailValidation.message);
        return;
      }
      finalIdentifier = identifier.toLowerCase().trim();
    } else {
      // ✅ JUSTIFIED: International phone validation requires libphonenumber-js
      // Auto-prepends +1 for US numbers if user doesn't include country code
      const phoneValidation = normalizePhoneForAuth(identifier);
      if (!phoneValidation.valid) {
        setError(phoneValidation.error || 'Invalid phone number');
        return;
      }
      finalIdentifier = phoneValidation.normalized!; // E.164 format: +12345678901
    }

    // Bot detection via honeypot
    if (botField) {
      logger.warn('Bot detected via honeypot, ignoring request');
      return;
    }

    // Check Turnstile verification for web
    if (!turnstileToken) {
      setError('Please complete the security check');
      return;
    }

    // Check cooldown
    if (otpCooldown > 0) {
      setError(`Please wait ${otpCooldown} seconds before requesting another code`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      logger.info(`Sending OTP via ${otpChannel} to: ${finalIdentifier}`);

      const callbackUrl = new URL(
        '/api/auth/callback',
        process.env.NEXT_PUBLIC_APP_URL || window.location.origin
      );
      if (safeRedirectTo) {
        callbackUrl.searchParams.set('next', safeRedirectTo);
      }

      // shouldCreateUser=false: don't silently create a half-account when an
      // unknown email/phone hits the OTP form. Net-new users got stranded with
      // no parents row and hit "User not authorized to create athletes" later
      // (see plan: got-new-support-ticket-vectorized-lampson.md). Real signup
      // starts at /register, which calls register_parent_atomic.
      const { data, error: otpError } = await supabase.auth.signInWithOtp({
        ...(otpChannel === 'email'
          ? { email: finalIdentifier }
          : { phone: finalIdentifier }
        ),
        options: {
          shouldCreateUser: false,
          emailRedirectTo: callbackUrl.toString(),
        }
      });

      if (otpError) {
        throw otpError;
      }

      setOtpSent(true);
      setOtpCooldown(120); // 120 second cooldown (matches OTP expiry)
      showNotification('success', 'Code Sent', `A verification code has been sent to your ${otpChannel}.`);
      logger.info('OTP sent successfully');

      // Reset Turnstile token
      setTurnstileToken(null);
    } catch (err: any) {
      logger.error('OTP send error:', err);
      // Supabase returns code='otp_disabled' (message: "Signups not allowed for otp")
      // when shouldCreateUser=false and the email/phone doesn't match an account.
      // Translate to a clear next-step instead of the raw GoTrue copy.
      const isUnknownAccount =
        err?.code === 'otp_disabled' ||
        /signups? not allowed/i.test(err?.message || '');
      if (isUnknownAccount) {
        setError(
          isParentInvitationRedirect && otpChannel === 'email'
            ? "We couldn't find that CheerVue account yet. Go back to your invite link and choose Create my account."
            : otpChannel === 'email'
            ? "We couldn't find an account for that email. Create your account from your gym's program page."
            : "We couldn't find an account for that phone number. Create your account from your gym's program page."
        );
      } else {
        setError(err.message || 'Failed to send verification code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (value: string, index: number) => {
    const digits = extractOtpDigits(value);
    const newOtp = digits.length > 1
      ? applyOtpDigits(otpCode, digits, index)
      : (() => {
          const nextOtp = [...otpCode];
          nextOtp[index] = digits[0] || '';
          return nextOtp;
        })();

    setOtpCode(newOtp);

    // Auto-focus next input
    if (digits.length > 1) {
      otpInputsRef.current[getOtpFocusIndex(otpCode, digits.length, index)]?.focus();
    } else if (digits[0] && index < otpInputsRef.current.length - 1) {
      otpInputsRef.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newOtp.every(digit => digit.length === 1)) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  // Handle OTP input keydown
  const handleOtpKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace') {
      const newOtp = [...otpCode];
      
      // If current input is empty, move to previous
      if (!otpCode[index] && index > 0) {
        otpInputsRef.current[index - 1]?.focus();
      }
      
      newOtp[index] = '';
      setOtpCode(newOtp);
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowRight' && index < otpInputsRef.current.length - 1) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (event: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    event.preventDefault();
    const pasteData = extractOtpDigits(event.clipboardData.getData('text'));
    if (pasteData.length === 0) return;

    const newOtp = applyOtpDigits(otpCode, pasteData, index);

    setOtpCode(newOtp);

    // Focus the last filled input
    const filledIndex = getOtpFocusIndex(otpCode, pasteData.length, index);
    if (otpInputsRef.current[filledIndex]) {
      otpInputsRef.current[filledIndex]?.focus();
    }

    // Auto-submit if complete
    if (newOtp.every(digit => digit.length === 1)) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  // Handle OTP verification
  const handleVerifyOTP = async (code?: string) => {
    const codeToVerify = code || otpCode.join('');
    
    if (codeToVerify.length !== OTP_CODE_LENGTH) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    if (isOtpVerifyingRef.current) return;

    isOtpVerifyingRef.current = true;
    setLoading(true);
    setError(null);
    const otpDestination = validateInternalPath(searchParams.get('redirectTo'), '/dashboard');
    rememberStaffPersonaForRedirect(otpDestination);

    try {
      // Normalize identifier same way as when we sent it
      let finalIdentifier = identifier.trim();
      if (otpChannel === 'sms') {
        const phoneValidation = normalizePhoneForAuth(identifier);
        if (phoneValidation.valid && phoneValidation.normalized) {
          finalIdentifier = phoneValidation.normalized;
        }
      } else {
        finalIdentifier = identifier.toLowerCase().trim();
      }
      
      logger.info('Verifying OTP for:', finalIdentifier);

      const { data: authData, error: verifyError } = await supabase.auth.verifyOtp({
        ...(otpChannel === 'email'
          ? { email: finalIdentifier }
          : { phone: finalIdentifier }
        ),
        token: codeToVerify,
        type: otpChannel === 'email' ? 'email' : 'sms'
      });

      if (verifyError) {
        throw verifyError;
      }

      if (!authData.user || !authData.session) {
        throw new Error('Authentication failed');
      }

      logger.info('OTP verification successful');

      // #5169: a code sign-in never passes a server step, so tell the server.
      // It sends the one-time open-bills notice if this sign-in just set up a
      // roster-imported family's account; for anyone else it does nothing.
      void fetch('/api/auth/first-sign-in-catchup', { method: 'POST', keepalive: true }).catch(() => {});

      // Check if user needs to change password
      if (authData.user?.user_metadata?.password_reset_required && 
          authData.user?.user_metadata?.password_reset_by_admin) {
        logger.info('User requires mandatory password change, redirecting...');
        router.push(changePasswordHref);
        return;
      }

      // Check if user needs MFA verification (2FA enabled)
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalData?.nextLevel === 'aal2' && aalData?.currentLevel === 'aal1') {
        logger.info("MFA required after OTP login, redirecting to verification page...");
        // safeRedirectTo is already validated at the top of the component.
        if (safeRedirectTo) {
          rememberStaffPersonaForRedirect(safeRedirectTo);
        }
        router.push(`/auth/mfa-verify${safeRedirectTo ? `?redirectTo=${encodeURIComponent(safeRedirectTo)}` : ''}`);
        return;
      }

      // Check for redirect URL (validated to block open-redirect attacks)
      logger.info(`Redirecting to ${otpDestination} after OTP login`);
      router.push(otpDestination);
    } catch (err: any) {
      forgetStaffPersonaForRedirect(otpDestination);
      logger.error('OTP verification error:', err);
      let errorMessage = 'Invalid or expired code. Please try again.';
      
      if (err.message?.includes('Token has expired')) {
        errorMessage = 'This code has expired. Please request a new one.';
        setOtpSent(false);
        setOtpCode(createEmptyOtpCode());
      } else if (err.message?.includes('Token invalid')) {
        errorMessage = 'Invalid code. Please check and try again.';
      }
      
      setError(errorMessage);
      setOtpCode(createEmptyOtpCode());
      otpInputsRef.current[0]?.focus();
    } finally {
      isOtpVerifyingRef.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Bot detection via honeypot
    if (botField) {
      logger.warn('Bot detected via honeypot in password login');
      return;
    }

    setLoading(true);
    setError(null);
    const signInDestination = validateInternalPath(searchParams.get('redirectTo'), '/dashboard');
    rememberStaffPersonaForRedirect(signInDestination);

    logger.info(`Attempting to sign in with email: ${email}`);

    try {
      const result = await signIn(email, password);
      logger.info("Sign in result:", result);
      
      if (!result.success) {
        throw result.error || new Error('An error occurred during sign in');
      }
      
      logger.info("Sign in successful, checking requirements...");

      // Check if user needs MFA verification (2FA enabled)
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalData?.nextLevel === 'aal2' && aalData?.currentLevel === 'aal1') {
        logger.info("MFA required, redirecting to verification page...");
        // safeRedirectTo is already validated at the top of the component.
        if (safeRedirectTo) {
          rememberStaffPersonaForRedirect(safeRedirectTo);
        }
        router.push(`/auth/mfa-verify${safeRedirectTo ? `?redirectTo=${encodeURIComponent(safeRedirectTo)}` : ''}`);
        return;
      }

      // Check if user needs to change password
      if (result.requiresPasswordChange) {
        logger.info("User requires mandatory password change, redirecting...");
        router.push(changePasswordHref);
        return;
      }

      // Check if user needs to complete onboarding (role selection)
      if (result.requiresOnboarding) {
        logger.info("User requires onboarding (role selection), redirecting...");
        router.push("/onboarding/select-role");
        return;
      }

      // A gym with unfinished settings is NOT sent to the settings checklist any more.
      //
      // This was the second of two redirects doing that; removing only the /dashboard
      // one left this path — the one an actual new owner takes — unchanged, so they were
      // still force-marched through five configuration screens before seeing their own
      // gym. It was also the worse of the two now: /dashboard/admin forwards to the first
      // incomplete settings step, which renders in focus mode with no app sidebar and
      // where the roster guide is deliberately suppressed. Owners fall through to the
      // normal dashboard route below, where the roster guide lives, and reach settings
      // from the "Finish Gym Settings" card.

      // Check for pending payment redirect from older account-verification flows.
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data: authUser } = await supabase
            .from('auth_users')
            .select('metadata')
            .eq('id', user.id)
            .single();

          if (authUser?.metadata?.pending_payment_redirect) {
            const { redirect_to, invoice_id } = authUser.metadata.pending_payment_redirect;
            
            // Check if email is verified (required for payment)
            if (user.email_confirmed_at) {
              logger.info('User has pending payment, redirecting to billing', { 
                userId: user.id, 
                invoiceId: invoice_id 
              });
              
              // Clear the pending flag
              await supabase
                .from('auth_users')
                .update({ 
                  metadata: { 
                    ...authUser.metadata, 
                    pending_payment_redirect: null 
                  } 
                })
                .eq('id', user.id);
              
              // Redirect to billing page with highlighted invoice.
              //
              // Validated like the sibling sink below, and for the same reason: this
              // value comes out of auth_users.metadata, which is writable cross-tenant
              // (#1609). Unvalidated, a planted redirect_to would navigate an owner
              // off-origin in the middle of signing in — an ideal credential-phish
              // landing. validateInternalPath falls back to /dashboard/billing rather
              // than following anything external.
              router.push(validateInternalPath(redirect_to, '/dashboard/billing'));
              return;
            } else {
              logger.info('User has pending payment but email not verified', { userId: user.id });
              setError('Please verify your email before completing payment. Check your inbox for the verification link.');
              setShowResendVerification(true);
              setLoading(false);
              return;
            }
          }
        }
      } catch (redirectError: any) {
        logger.error('Error checking pending payment redirect:', redirectError);
        // Continue with normal login flow if this fails
      }
      
      // Check if there's a redirect URL to return to (validated to block open-redirect attacks)
      logger.info(`Redirecting to ${signInDestination} after login for role-based routing`);
      router.push(signInDestination);
    } catch (err) {
      forgetStaffPersonaForRedirect(signInDestination);
      logger.error("Sign in error:", err);
      
      // More detailed error handling
      let errorMessage = 'Invalid email or password';
      let showResend = false;
      
      if (err instanceof Error) {
        errorMessage = err.message;
        
        // Check for specific Supabase error types
        const errObj = err as any;
        
        // Check for email verification errors
        if (errObj.message?.includes('Email not confirmed') || 
            errObj.message?.includes('email not confirmed') ||
            errObj.message?.includes('Email not verified')) {
          errorMessage = 'Please verify your email before signing in. Check your inbox for the verification link.';
          showResend = true;
        } else if (errObj.code === 'invalid_credentials') {
          errorMessage = 'Invalid email or password. Please try again.';
        } else if (errObj.code === 'invalid_grant') {
          errorMessage = 'Your session has expired. Please sign in again.';
        }
      }
      
      setError(errorMessage);
      setShowResendVerification(showResend);
    } finally {
      setLoading(false);
    }
  };

  // Handle resend verification email
  // Uses app's custom email system (SendGrid) so EMAIL_TEST_OVERRIDE is respected in dev
  const handleResendVerification = async () => {
    if (!email) {
      showNotification('error', 'Email Required', 'Please enter your email address first.');
      return;
    }

    setIsResending(true);

    try {
      const response = await fetch('/api/public/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          redirectTo: safeRedirectTo || undefined,
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification email');
      }

      showNotification('success', 'Email Sent!', 'Please check your inbox for the verification link.');
      setShowResendVerification(false);
    } catch (err: any) {
      showNotification('error', 'Failed to Send Email', err.message);
    } finally {
      setIsResending(false);
    }
  };

  // Handle resend setup/recovery link
  const handleResendSetup = async () => {
    if (!email) {
      showNotification('error', 'Email Required', 'Please enter your email address first.');
      return;
    }

    setIsResending(true);
    
    try {
      // Use the dedicated setup-specific resend API
      // This sends the friendly "Welcome" email instead of a scary "Password Reset"
      //
      // This handler only runs from a button the owner pressed on the
      // "Your link is no longer valid" panel, so it is the same asked-for-it
      // moment as /onboarding/setup-link-expired and must get the same
      // answer-shaped email — not the unprompted "we noticed you haven't
      // finished setting up" nudge.
      const response = await fetch('/api/public/auth/signup/organization/resend-setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, requestedByOwner: true })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send setup link');
      }
      
      showNotification(
        'success', 
        'Link Sent!', 
        'We\'ve sent a fresh setup link to your email. Please check your inbox (and spam folder) to continue.'
      );
      setShowResendSuccess(true);
    } catch (err: any) {
      logger.error('Failed to resend setup link:', err);
      showNotification('error', 'Failed to Send Link', err.message);
    } finally {
      setIsResending(false);
    }
  };

  // Handle resend of an expired password-reset link.
  //
  // Deliberately NOT the setup resend: this person already has a password, and
  // the setup endpoint would mint an owner-setup link and email them "Your new
  // CheerVue setup link". Same endpoint the Forgot Password page uses, so both
  // routes to a fresh reset send the identical email.
  const handleResendPasswordReset = async () => {
    if (!email) {
      showNotification('error', 'Email Required', 'Please enter your email address first.');
      return;
    }

    setIsResending(true);

    try {
      const response = await fetch('/api/auth/reset-password-v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send password reset link');
      }

      showNotification(
        'success',
        'Link Sent!',
        'We\'ve sent a fresh password reset link to your email. Please check your inbox (and spam folder) to continue.'
      );
      setShowResendSuccess(true);
    } catch (err: any) {
      logger.error('Failed to resend password reset link:', err);
      showNotification('error', 'Failed to Send Link', err.message);
    } finally {
      setIsResending(false);
    }
  };

  // Every string on the expired-link panel, chosen once from which flavour of
  // link died. Kept together so the two halves cannot drift into a panel that
  // says "reset" at the top and "setup" on the button.
  const expiredLinkPanelCopy =
    expiredLinkPanel === 'reset'
      ? {
          title: 'Reset Link Expired',
          subtitle:
            'Your link is no longer valid. Enter your email and we\'ll send you a new password reset link.',
          buttonLabel: 'Send New Reset Link',
          // Both halves of the first sentence are load-bearing: this panel
          // exists for people who opened the email too late, so a footnote
          // blaming only "once they've been used" described the rarer of the
          // two ways their link died. No duration, because the real one is a
          // Supabase setting that would go stale here the day it changes.
          footnote:
            `For security, password reset links can only be used once, and they expire ${AUTH_LINK_LIFETIME_TEXT} after they're sent. Resending gives you a fresh one.`,
          successTitle: 'Reset Link Sent!',
          // True only because resets now land on the Set New Password screen.
          // While they went to /setup this sentence would have been a lie —
          // that stub forwards to the dashboard without asking for anything.
          successBody: 'Please click the link in that email to choose a new password.',
          onSend: handleResendPasswordReset,
        }
      : {
          title: 'Setup Link Expired',
          subtitle:
            'Your link is no longer valid. Enter your email to receive a fresh setup link.',
          buttonLabel: 'Send Fresh Setup Link',
          footnote:
            `For security, setup links can only be used once, and they expire ${AUTH_LINK_LIFETIME_TEXT} after they're sent. Resending will provide a fresh link to finish your gym profile.`,
          successTitle: 'Setup Link Sent!',
          successBody: 'Please click the link in that email to finish setting up your gym.',
          onSend: handleResendSetup,
        };

  return (
    // Requirements summary: mobile Safari should scroll the auth document,
    // while desktop preserves the split-screen pane scroll behavior.
    // Column geometry here must stay in sync with AuthFormLoading, which stands
    // in for this form while it loads.
    <div className="flex flex-col w-full lg:w-1/2 lg:flex-1 lg:overflow-y-auto lg:no-scrollbar">
      {/* Honeypot field to catch bots - hidden from humans */}
      <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
        <input
          type="text"
          name="b_n0t_huma_n"
          tabIndex={-1}
          autoComplete="off"
          onChange={(e) => setBotField(e.target.value)}
          value={botField}
        />
      </div>
      <div className="w-full max-w-md sm:pt-10 mx-auto mb-5">
      </div>
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div>
          {/* Mobile Logo - Only visible on tablet and below (< 1024px) */}
          <div className="lg:hidden flex items-center justify-center mb-6 sm:mb-8">
            <Link href="/" className="block">
              <GradientLogo 
                variant="full" 
                width={300}
                height={63}
                useGradient={brandTheme === 'v2'}
                className="h-20 w-auto sm:h-24"
              />
            </Link>
          </div>
          
          <div className="mb-5 sm:mb-8">
            <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
              {showResendSuccess
                ? 'Check Your Email'
                : showResendSetup
                ? expiredLinkPanelCopy.title
                : otpSent
                ? 'Enter Verification Code'
                : bookingContext === 'booking'
                ? 'Sign in to book your lesson'
                : bookingContext === 'enrollment'
                ? 'Sign in to enroll'
                : bookingContext === 'registration'
                ? 'Sign in to register'
                : isParentInvitationRedirect
                ? 'Sign in to join your gym'
                : 'Sign In'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {showResendSuccess
                ? 'We\'ve sent a fresh link to your inbox.'
                : showResendSetup
                ? expiredLinkPanelCopy.subtitle
                : otpSent
                ? `Enter the 6-digit code sent to your ${otpChannel === 'email' ? 'email' : 'phone'}.`
                : isParentInvitationRedirect
                ? 'Use the email from your invite. We’ll send you straight back to finish joining the gym.'
                : bookingContext
                ? 'We\'ll send you straight back after you sign in.'
                : loginMode === 'otp'
                ? `Enter your ${otpChannel === 'email' ? 'email' : 'phone number'} to receive a verification code.`
                : 'Enter your email and password to sign in!'}
            </p>
          </div>
          
          {message && !showResendSetup && !showResendSuccess && (
            <div className="mb-4 p-3 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 rounded-lg text-sm">
              {message}
            </div>
          )}
          
          <div>
            {showResendSuccess ? (
              <div className="space-y-6">
                <div className="p-6 bg-green-50 dark:bg-green-500/5 border border-green-100 dark:border-green-500/10 rounded-2xl text-center">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <GoogleMaterialIcon name="mark_email_read" className="text-green-600 dark:text-green-400" size="lg" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{expiredLinkPanelCopy.successTitle}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    A fresh link has been sent to <strong className="text-gray-900 dark:text-white">{email}</strong>.{' '}
                    {expiredLinkPanelCopy.successBody}
                  </p>

                  <div className="space-y-3">
                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={() => {
                        setShowResendSuccess(false);
                        setExpiredLinkPanel(null);
                        setError(null);
                      }}
                    >
                      Return to Sign In
                    </Button>
                    <p className="text-xs text-gray-500 dark:text-gray-500">
                      Didn't get the email? Check your spam folder or try again in a few minutes.
                    </p>
                  </div>
                </div>
              </div>
            ) : !showResendSetup ? (
              <>
                {/* Login mode toggle link is placed below the action button in each form */}

                {/* Password Login Form */}
                {loginMode === 'password' && !otpSent && (
                  <form onSubmit={handleSubmit}>
                    <div className="space-y-5 px-1 sm:px-0">
                      <div className="overflow-visible">
                        <TextField
                          id="email"
                          name="email"
                          label="Email"
                          type="email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="overflow-visible">
                        <PasswordField
                          id="password"
                          name="password"
                          label="Password"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          required
                        />
                      </div>
                      <div className="flex items-center justify-end">
                        <Link
                          href="/auth/forgot-password"
                          className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      {error && (
                        <div className="text-error-500 text-sm text-center">{error}</div>
                      )}
                      <div>
                        <Button className="w-full" type="submit" disabled={loading}>
                          {loading ? "Signing in..." : "Sign in"}
                        </Button>
                      </div>

                      {/* Toggle to OTP mode */}
                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => {
                            setLoginMode('otp');
                            setError(null);
                          }}
                          className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                        >
                          Sign in with a verification code instead
                        </button>
                      </div>

                      {/* Resend Verification Email Section */}
                      {showResendVerification && (
                        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                            Didn't receive the verification email?
                          </p>
                          <Button
                            onClick={handleResendVerification}
                            disabled={isResending || !email}
                            variant="outline"
                            className="w-full"
                          >
                            {isResending ? 'Sending...' : 'Resend Verification Email'}
                          </Button>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                            Make sure to check your spam folder if you don't see the email.
                          </p>
                        </div>
                      )}
                    </div>
                  </form>
                )}

                {/* OTP Login Form */}
                {loginMode === 'otp' && !otpSent && (
                  <div className="space-y-5 px-1 sm:px-0">
                    {/* Channel Selection - Only show if SMS OTP is enabled */}
                    {FEATURE_FLAGS.ENABLE_SMS_OTP && (
                      <div className="flex gap-2 p-1 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <button
                          type="button"
                          onClick={() => {
                            setOtpChannel('email');
                            setIdentifier('');
                            setError(null);
                          }}
                          className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                            otpChannel === 'email'
                              ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <GoogleMaterialIcon name="email" size="xs" className="inline mr-2" />
                          Email
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setOtpChannel('sms');
                            setIdentifier('');
                            setError(null);
                          }}
                          className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                            otpChannel === 'sms'
                              ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <GoogleMaterialIcon name="sms" size="xs" className="inline mr-2" />
                          SMS
                        </button>
                      </div>
                    )}

                    <div className="overflow-visible">
                      {otpChannel === 'email' ? (
                        <EmailField
                          id="identifier"
                          name="identifier"
                          label="Email"
                          placeholder="Enter your email"
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                          required
                          checkDuplicates={false} // Don't check duplicates during login
                        />
                      ) : (
                        <PhoneField
                          id="identifier"
                          name="identifier"
                          label="Phone Number"
                          placeholder="(123) 456-7890"
                          value={identifier}
                          onChange={(e) => setIdentifier(e.target.value)}
                          required
                        />
                      )}
                      {otpChannel === 'sms' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          We'll automatically handle the country code for US numbers.
                        </p>
                      )}
                    </div>

                    {/* Cloudflare Turnstile CAPTCHA - Invisible by default, only shows if challenge required */}
                    <div className="flex justify-center empty:hidden">
                      <Turnstile
                        sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                        onVerify={(token) => setTurnstileToken(token)}
                        onError={() => setTurnstileToken(null)}
                        onExpire={() => setTurnstileToken(null)}
                        theme={brandTheme === 'v1' ? 'light' : 'auto'}
                        appearance="interaction-only"
                      />
                    </div>

                    {error && (
                      <div className="text-error-500 text-sm text-center">{error}</div>
                    )}

                    <div>
                      <Button
                        className="w-full"
                        onClick={handleSendOTP}
                        disabled={loading || otpCooldown > 0 || !turnstileToken}
                      >
                        {loading ? "Sending Code..." : otpCooldown > 0 ? `Wait ${otpCooldown}s` : "Continue"}
                      </Button>
                    </div>

                    {/* Toggle to password mode */}
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => {
                          setLoginMode('password');
                          setError(null);
                        }}
                        className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                      >
                        Sign in with password instead
                      </button>
                    </div>

                    {otpChannel === 'sms' && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                        ⚠️ SMS rates may apply.
                      </p>
                    )}
                  </div>
                )}

                {/* OTP Verification Form */}
                {otpSent && (
                  <div className="space-y-5 px-1 sm:px-0">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Enter 6-digit code
                      </label>
                      <div className="flex gap-2 sm:gap-4 justify-center">
                        {otpCode.map((digit, index) => (
                          <input
                            key={index}
                            ref={(el) => {
                              otpInputsRef.current[index] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            autoComplete={index === 0 ? "one-time-code" : "off"}
                            maxLength={index === 0 ? OTP_CODE_LENGTH : 1}
                            aria-label={`Verification code digit ${index + 1}`}
                            disabled={loading}
                            value={digit}
                            onChange={(e) => {
                              handleOtpChange(e.target.value, index);
                            }}
                            onKeyDown={(e) => handleOtpKeyDown(e, index)}
                            onPaste={(e) => handleOtpPaste(e, index)}
                            className="w-12 h-12 sm:w-14 sm:h-14 text-center text-xl font-semibold border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all"
                          />
                        ))}
                      </div>
                    </div>

                    {error && (
                      <div className="text-error-500 text-sm text-center">{error}</div>
                    )}

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => {
                          setOtpSent(false);
                          setOtpCode(createEmptyOtpCode());
                          setError(null);
                        }}
                        className="text-sm text-gray-600 dark:text-gray-400 hover:text-brand-500 dark:hover:text-brand-400"
                      >
                        ← Back
                      </button>
                    </div>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={handleSendOTP}
                        disabled={otpCooldown > 0 || loading}
                        className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {otpCooldown > 0 ? `Resend code in ${otpCooldown}s` : 'Resend code'}
                      </button>
                    </div>

                    {loading && (
                      <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                        Verifying code...
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-6">
                {error && (
                  <div className="p-4 bg-red-50 text-red-600 text-sm font-semibold rounded-xl border border-red-100 flex items-center gap-3">
                    <GoogleMaterialIcon name="error" size="sm" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-5">
                  <TextField
                    id="setup-email"
                    name="email"
                    label="Email Address"
                    type="email"
                    placeholder="Enter your registered email"
                    value={email}
                    onChange={handleChange}
                    required
                  />
                  
                  <div className="pt-2">
                    <Button
                      onClick={expiredLinkPanelCopy.onSend}
                      disabled={isResending || !email}
                      variant="primary"
                      className="w-full py-4 text-lg"
                    >
                      {isResending ? 'Sending...' : expiredLinkPanelCopy.buttonLabel}
                    </Button>
                  </div>

                  <button
                    onClick={() => {
                      setExpiredLinkPanel(null);
                      setError(null);
                    }}
                    className="w-full text-center text-sm text-gray-500 hover:text-brand-500 transition-colors"
                  >
                    Back to Sign In
                  </button>
                </div>

                <div className="p-4 bg-brand-50 dark:bg-brand-500/5 border border-brand-100 dark:border-brand-500/10 rounded-2xl">
                  <p className="text-xs text-brand-800 dark:text-brand-400 leading-relaxed text-center">
                    {expiredLinkPanelCopy.footnote}
                  </p>
                </div>
              </div>
            )}
            
            {!showResendSetup && !showResendSuccess && (
              <div className="mt-5">
                <p className="text-sm font-normal text-center text-gray-700 dark:text-gray-400 sm:text-start">
                  Don&apos;t have an account? Create one from{" "}
                  {gymCatalogHref ? (
                    <Link
                      href={gymCatalogHref}
                      className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                    >
                      your gym&apos;s program page
                    </Link>
                  ) : (
                    <>your gym&apos;s program page</>
                  )}
                  , or ask your gym to send you an invite.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
